import { detectEnvironment } from './config/environment';
import { ApiClient } from './resources/apiClient';
import { FunnelAction, FunnelActionType, FunnelNavigationResult, FunnelResource, SimpleFunnelContext } from './resources/funnel';
import { EventDispatcher } from './utils/eventDispatcher';
import { PluginConfig } from './utils/pluginConfig';
import { getSDKParams } from './utils/previewMode';
const lazyInjectPreviewIndicator = () =>
  import('./utils/previewModeIndicator').then(m => m.injectPreviewModeIndicator());
import { getFunnelSessionCookie, setFunnelSessionCookie } from './utils/sessionStorage';

/**
 * Tracking providers supported by the SDK.
 * Mirrors `TrackingType` in `apps/crm-app/.../types/tracking.ts`.
 */
export enum TrackingProvider {
  FACEBOOK = 'facebook',
  TIKTOK = 'tiktok',
  SNAPCHAT = 'snapchat',
  PINTEREST = 'pinterest',
  GTM = 'gtm',
}

/**
 * Base tracking config (all providers)
 */
export interface BaseTrackingConfig {
  enabled: boolean;
}

/**
 * Pixel-based providers (Facebook, TikTok)
 */
export interface PixelTrackingConfig extends BaseTrackingConfig {
  pixelId: string;
  events: {
    PageView: boolean;
    InitiateCheckout: boolean;
    Purchase: boolean;
  };
}

/**
 * Snapchat pixel config (extends base pixel events)
 */
export interface SnapchatTrackingConfig extends BaseTrackingConfig {
  pixelId: string;
  events: {
    PageView: boolean;
    InitiateCheckout: boolean;
    Purchase: boolean;
    AddToCart: boolean;
    ViewContent: boolean;
    Search: boolean;
    AddToWishlist: boolean;
    CompleteRegistration: boolean;
  };
}

/**
 * Meta Conversion API config
 */
export interface MetaConversionTrackingConfig extends BaseTrackingConfig {
  accessToken: string;
  pixelId: string;
  publishPurchaseIfNewCustomerOnly: boolean;
}

/**
 * Google Tag Manager config
 */
export interface GTMTrackingConfig extends BaseTrackingConfig {
  containerId: string;
  events: {
    PageView: boolean;
    InitiateCheckout: boolean;
    Purchase: boolean;
    AddToCart: boolean;
    ViewContent: boolean;
  };
  googleAdsConversionId?: string;
  googleAdsConversionLabel?: string;
}

/**
 * Union of all tracking configs.
 * This mirrors `TrackingFormValues` from the CRM.
 */
export type PixelConfig =
  | PixelTrackingConfig
  | SnapchatTrackingConfig
  | MetaConversionTrackingConfig
  | GTMTrackingConfig;

/**
 * Pixels configuration keyed by provider.
 * Used in RuntimeStepConfig and getAssignedPixels().
 */
export type PixelsConfig = {
  [TrackingProvider.FACEBOOK]?: PixelTrackingConfig[];
  [TrackingProvider.TIKTOK]?: PixelTrackingConfig[];
  [TrackingProvider.SNAPCHAT]?: SnapchatTrackingConfig[];
  [TrackingProvider.PINTEREST]?: PixelTrackingConfig[];
  [TrackingProvider.GTM]?: GTMTrackingConfig[];
};

/**
 * Payment method configuration entry.
 *
 * Key pattern in paymentSetupConfig:
 *   "{method}" or "{method}:{provider}"
 *   e.g. "card", "apple_pay:stripe", "paypal:tagada", "klarna:stripe"
 *
 * `method`   = what (universal payment method: card, apple_pay, klarna, ideal...)
 * `provider` = who (processor family: tagada, stripe, airwallex, adyen...)
 *
 * @deprecated PaymentSetupMethod — use PaymentMethodConfig instead
 */
export interface ExpressMethodState {
  enabled: boolean;
}

export type PaymentSetupMethod = PaymentMethodConfig;

export interface PaymentMethodConfig {
  enabled: boolean;
  /** Universal payment method identifier */
  method: string;
  /** Provider / processor family */
  provider: string;
  /** e.g. 'apm' for alternative payment methods, 'card', etc. */
  type?: string;
  /** Display label for the payment method */
  label?: string;
  /** URL to the payment method's logo */
  logoUrl?: string;
  /** Human-readable description shown to the customer */
  description?: string;
  /** Render as express checkout button above the form */
  express?: boolean;
  /** Card routing via payment flow (cascade, fraud, processor selection) */
  paymentFlowId?: string;
  /** Direct processor routing for APMs and express methods */
  processorId?: string;
  /** Tagada integration record ID for native integrations */
  integrationId?: string;
  supportedCountries?: string[];
  presentmentCurrencies?: string[];
  recurring?: boolean;
  /** Present only on express_checkout:proc_id entries */
  publishableKey?: string;
  /** Present only on express_checkout:proc_id entries — map of apmKey → state */
  methods?: Record<string, ExpressMethodState>;
  /** Present only on crypto integration entries — map of providerId → provider state */
  providers?: Record<string, { enabled?: boolean; label?: string; min?: number; note?: string }>;
  metadata?: Record<string, any>;
  /** Integration settings (e.g. custom_payment checkout instructions, thank-you text) */
  settings?: Record<string, any>;
}

// ─── Payment Setup Config Helpers ─────────────────────────────────

export type PaymentSetupConfig = Record<string, PaymentMethodConfig>;

/** Get all enabled entries from a paymentSetupConfig */
export function getEnabledMethods(config: PaymentSetupConfig | undefined): PaymentMethodConfig[] {
  if (!config) return [];
  return Object.values(config).filter(m => m.enabled);
}

/** Get all express methods (for rendering above the payment form) */
export function getExpressMethods(config: PaymentSetupConfig | undefined): PaymentMethodConfig[] {
  return getEnabledMethods(config).filter(m => m.express);
}

/** Group enabled express methods by processorId (one Express Element per processor) */
export function getExpressMethodsByProcessor(config: PaymentSetupConfig | undefined): Record<string, PaymentMethodConfig[]> {
  const groups: Record<string, PaymentMethodConfig[]> = {};
  for (const m of getExpressMethods(config)) {
    const key = m.processorId || m.integrationId || m.provider;
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  }
  return groups;
}

/** Find a specific method entry by method name (returns first match) */
export function findMethod(config: PaymentSetupConfig | undefined, method: string): PaymentMethodConfig | undefined {
  if (!config) return undefined;
  for (const entry of Object.values(config)) {
    if (entry.method === method && entry.enabled) return entry;
  }
  return undefined;
}

/** Check if a specific method is enabled (any provider) */
export function isMethodEnabled(config: PaymentSetupConfig | undefined, method: string): boolean {
  return !!findMethod(config, method);
}

export interface RuntimeStepConfig {
  payment?: {
    paymentFlowId?: string;
  };
  paymentSetupConfig?: PaymentSetupConfig;
  orderBumps?: {
    mode: 'inherit' | 'custom';
    enabledOfferIds?: string[];
  };
  upsellOffers?: {
    mode: 'inherit' | 'custom';
    enabledUpsellIds?: string[];
  };
  /** @deprecated Use `resources` instead */
  staticResources?: Record<string, string>;
  /** Typed resource bindings (replaces staticResources) */
  resources?: Record<string, string>;
  scripts?: Array<{
    name: string;
    enabled: boolean;
    content: string;
    position?: 'head-start' | 'head-end' | 'body-start' | 'body-end';
  }>;
  pixels?: PixelsConfig;
}

/**
 * Get the funnel ID from the injected HTML
 * Returns undefined if not available
 */
function getAssignedFunnelId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  // Method 1: Window variable (preferred - synchronous, fast)
  if ((window as any).__TGD_FUNNEL_ID__) {
    return (window as any).__TGD_FUNNEL_ID__;
  }

  // Method 2: Meta tag fallback
  if (typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="x-funnel-id"]');
    return meta?.getAttribute('content') || undefined;
  }

  return undefined;
}

/**
 * Get the assigned A/B test variant ID from the injected HTML
 * Returns undefined if not in an A/B test
 */
function getAssignedFunnelVariant(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  // Method 1: Window variable (preferred - synchronous, fast)
  if ((window as any).__TGD_FUNNEL_VARIANT_ID__) {
    return (window as any).__TGD_FUNNEL_VARIANT_ID__;
  }

  // Method 2: Meta tag fallback
  if (typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="x-funnel-variant-id"]');
    return meta?.getAttribute('content') || undefined;
  }

  return undefined;
}

/**
 * Get the funnel step ID from the injected HTML
 * Returns undefined if not available
 */
function getAssignedFunnelStep(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  // Method 1: Window variable (preferred - synchronous, fast)
  if ((window as any).__TGD_FUNNEL_STEP_ID__) {
    return (window as any).__TGD_FUNNEL_STEP_ID__;
  }

  // Method 2: Meta tag fallback
  if (typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="x-funnel-step-id"]');
    return meta?.getAttribute('content') || undefined;
  }

  return undefined;
}

/**
 * Parse step config from a string value (handles both JSON and URL-encoded formats)
 * Robust parsing with multiple fallback strategies
 */
function parseStepConfig(value: string): RuntimeStepConfig | undefined {
  if (!value || typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Try parsing strategies in order of likelihood
  const strategies = [
    // Strategy 1: Direct JSON parse (for properly escaped window variable)
    () => JSON.parse(trimmed),
    // Strategy 2: URL decode then JSON parse (for meta tag)
    () => JSON.parse(decodeURIComponent(trimmed)),
    // Strategy 3: Double URL decode (edge case: double-encoded)
    () => JSON.parse(decodeURIComponent(decodeURIComponent(trimmed))),
  ];

  for (const strategy of strategies) {
    try {
      const result = strategy();
      if (result && typeof result === 'object') {
        return result as RuntimeStepConfig;
      }
    } catch {
      // Try next strategy
    }
  }

  // All strategies failed
  if (typeof console !== 'undefined') {
    console.warn('[SDK] Failed to parse stepConfig:', trimmed.substring(0, 100));
  }
  return undefined;
}

/**
 * Local funnel configuration for development
 * Loaded from /config/funnel.local.json
 */
export interface LocalFunnelConfig {
  /** Funnel ID to use in local dev */
  funnelId?: string;
  /** Step ID to simulate */
  stepId?: string;
  /** @deprecated Use `resources` instead */
  staticResources?: Record<string, string>;
  /** Resource bindings (offer ID, product ID, etc.) */
  resources?: Record<string, string>;
  /** Payment flow ID override */
  paymentFlowId?: string;
  /** Custom scripts for local testing */
  scripts?: RuntimeStepConfig['scripts'];
  /** Pixel tracking config */
  pixels?: RuntimeStepConfig['pixels'];
  /** Order bump filtering config for local testing */
  orderBumps?: RuntimeStepConfig['orderBumps'];
  /** Upsell offer filtering config for local testing */
  upsellOffers?: RuntimeStepConfig['upsellOffers'];
}

// Cache for local funnel config (loaded once)
let localFunnelConfigCache: LocalFunnelConfig | null | undefined = undefined;
let localFunnelConfigLoading = false;

/**
 * Check if we're in true local development (not CDN deployment)
 */
function isLocalDevelopment(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  // True local: localhost without CDN subdomain pattern
  return hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    (hostname.endsWith('.localhost') && !hostname.includes('.cdn.'));
}

/**
 * Load local funnel config from /config/funnel.local.json (for local dev only)
 * This replaces the old resources.static.json with a more complete structure
 * 
 * Example funnel.local.json:
 * {
 *   "funnelId": "funnelv2_xxx",
 *   "stepId": "step_checkout",
 *   "staticResources": {
 *     "offer": "offer_xxx",
 *     "product": "product_xxx"
 *   },
 *   "paymentFlowId": "flow_xxx"
 * }
 */
export async function loadLocalFunnelConfig(): Promise<LocalFunnelConfig | null> {
  // Only in true local development
  if (!isLocalDevelopment()) return null;

  // Return cached value if already loaded
  if (localFunnelConfigCache !== undefined) {
    return localFunnelConfigCache;
  }

  // Prevent concurrent loads
  if (localFunnelConfigLoading) {
    // Wait for existing load
    await new Promise(resolve => setTimeout(resolve, 100));
    return localFunnelConfigCache ?? null;
  }

  localFunnelConfigLoading = true;

  try {
    console.log('🛠️ [SDK] Loading local funnel config from /config/funnel.local.json...');
    const response = await fetch('/config/funnel.local.json');
    if (!response.ok) {
      console.log('🛠️ [SDK] funnel.local.json not found (this is fine in production)');
      localFunnelConfigCache = null;
      return null;
    }

    const config = await response.json() as LocalFunnelConfig;
    console.log('🛠️ [SDK] ✅ Loaded local funnel config:', config);
    localFunnelConfigCache = config;
    return config;
  } catch (error) {
    console.log('🛠️ [SDK] funnel.local.json not available:', error);
    localFunnelConfigCache = null;
    return null;
  } finally {
    localFunnelConfigLoading = false;
  }
}

/**
 * Get the cached local funnel config (sync access after loadLocalFunnelConfig)
 */
export function getLocalFunnelConfig(): LocalFunnelConfig | null {
  return localFunnelConfigCache ?? null;
}

/**
 * Convert LocalFunnelConfig to RuntimeStepConfig
 */
function localConfigToStepConfig(local: LocalFunnelConfig): RuntimeStepConfig {
  const merged = { ...local.staticResources, ...local.resources };
  return {
    payment: local.paymentFlowId ? { paymentFlowId: local.paymentFlowId } : undefined,
    resources: Object.keys(merged).length > 0 ? merged : undefined,
    scripts: local.scripts,
    pixels: local.pixels,
    orderBumps: local.orderBumps,
    upsellOffers: local.upsellOffers,
  };
}

/**
 * Get the runtime step configuration
 * Contains payment flow, static resources, scripts, and pixel tracking
 * 
 * Priority:
 * 1. Local funnel config (local dev only - /config/funnel.local.json) - HIGHEST in local dev
 * 2. Window variable (production - HTML injection)
 * 3. Meta tag (production - HTML injection fallback)
 * 
 * This allows local developers to override injected config for testing.
 * 
 * Returns undefined if not available
 */
export function getAssignedStepConfig(): RuntimeStepConfig | undefined {
  if (typeof window === 'undefined') return undefined;

  // Method 1: Local dev override (HIGHEST PRIORITY in local dev)
  const localConfig = getLocalFunnelConfig();
  if (localConfig) {
    console.log('🛠️ [SDK] Using local funnel.local.json (overrides injected)');
    return localConfigToStepConfig(localConfig);
  }

  // Method 2: Window variable (production - HTML injection)
  const windowValue = (window as any).__TGD_STEP_CONFIG__;
  if (windowValue) {
    const parsed = parseStepConfig(windowValue);
    if (parsed) return parsed;
  }

  // Method 3: Meta tag fallback (URL-encoded)
  if (typeof document !== 'undefined') {
    const meta = document.querySelector('meta[name="x-step-config"]');
    const content = meta?.getAttribute('content');
    if (content) {
      const parsed = parseStepConfig(content);
      if (parsed) return parsed;
    }
  }

  return undefined;
}

/**
 * Get the assigned payment flow ID from step config or legacy injection.
 * Resolution order:
 *   1. paymentSetupConfig.card.paymentFlowId  (per-method, most specific)
 *   2. stepConfig.payment.paymentFlowId        (per-step level)
 *   3. window.__TGD_PAYMENT_FLOW_ID__          (legacy injection)
 *   4. meta[name="x-payment-flow-id"]          (legacy meta tag)
 */
export function getAssignedPaymentFlowId(): string | undefined {
  const stepConfig = getAssignedStepConfig();

  // Method 1: Per-method config — card entry in paymentSetupConfig
  const cardConfig = stepConfig?.paymentSetupConfig?.card;
  if (cardConfig?.paymentFlowId) {
    return cardConfig.paymentFlowId;
  }

  // Method 2: Per-step config (legacy but still supported)
  if (stepConfig?.payment?.paymentFlowId) {
    return stepConfig.payment.paymentFlowId;
  }

  // Method 3: Legacy direct injection (backward compatibility)
  if (typeof window !== 'undefined') {
    if ((window as any).__TGD_PAYMENT_FLOW_ID__) {
      return (window as any).__TGD_PAYMENT_FLOW_ID__;
    }
    if (typeof document !== 'undefined') {
      const meta = document.querySelector('meta[name="x-payment-flow-id"]');
      return meta?.getAttribute('content') || undefined;
    }
  }

  return undefined;
}

/**
 * Get resource bindings from step config.
 * Reads from `resources` (new) with `staticResources` (legacy) as fallback, merged.
 */
export function getAssignedResources(): Record<string, string> | undefined {
  const stepConfig = getAssignedStepConfig();
  const legacy = stepConfig?.staticResources;
  const current = stepConfig?.resources;
  if (!legacy && !current) return undefined;
  return { ...legacy, ...current };
}

/**
 * @deprecated Use `getAssignedResources()` instead.
 * Kept for backward compatibility — reads from both `resources` and `staticResources`.
 */
export function getAssignedStaticResources(): Record<string, string> | undefined {
  return getAssignedResources();
}

/**
 * Get the assigned scripts from step config
 * Returns only enabled scripts, filtered by position if specified
 */
export function getAssignedScripts(position?: 'head-start' | 'head-end' | 'body-start' | 'body-end'): RuntimeStepConfig['scripts'] {
  const stepConfig = getAssignedStepConfig();
  if (!stepConfig?.scripts) return undefined;

  // Filter enabled scripts
  let scripts = stepConfig.scripts.filter(s => s.enabled);

  // Filter by position if specified
  if (position) {
    scripts = scripts.filter(s => s.position === position || (!s.position && position === 'head-end'));
  }

  return scripts.length > 0 ? scripts : undefined;
}

/**
 * Get assigned pixel tracking configuration (normalized to arrays)
 * Always returns arrays of PixelConfig for consistent consumption.
 */
/**
 * Split a pixel config with semicolon/comma-separated IDs into individual configs.
 * Handles cases where the CRM stores "ID1; ID2; ID3" as a single pixelId or containerId.
 */
function splitPixelConfig(px: PixelConfig): PixelConfig[] {
  // GTM configs use containerId
  const idField = 'containerId' in px ? 'containerId' : 'pixelId';
  const rawId = (px as any)[idField] as string | undefined;
  if (!rawId || !rawId.includes(';') && !rawId.includes(',')) return [px];

  const ids = rawId.split(/[;,]/).map((id: string) => id.trim()).filter((id: string) => id.length > 0);
  if (ids.length <= 1) return [px];

  return ids.map((id) => ({ ...px, [idField]: id }));
}

export function getAssignedPixels(): PixelsConfig | undefined {
  const stepConfig = getAssignedStepConfig();
  const rawPixels = stepConfig?.pixels;
  if (!rawPixels || typeof rawPixels !== 'object') return undefined;

  const normalized: Record<string, PixelConfig[]> = {};

  for (const [key, value] of Object.entries(rawPixels)) {
    if (!value) continue;

    if (Array.isArray(value)) {
      // Expand any entries with semicolon/comma-separated pixelIds into separate configs
      normalized[key] = (value as PixelConfig[]).flatMap((px) => splitPixelConfig(px));
    } else if (typeof value === 'object') {
      // Single object - wrap in array, splitting if needed
      normalized[key] = splitPixelConfig(value as PixelConfig);
    }
    // Skip invalid entries
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Get the enabled order bump offer IDs from step config.
 * Returns undefined when mode is 'inherit' (use all store bumps).
 * Returns the explicit list when mode is 'custom'.
 */
export function getAssignedOrderBumpOfferIds(): string[] | undefined {
  const stepConfig = getAssignedStepConfig();
  if (!stepConfig?.orderBumps) return undefined;
  if (stepConfig.orderBumps.mode !== 'custom') return undefined;
  return stepConfig.orderBumps.enabledOfferIds;
}

/**
 * Get the enabled upsell offer IDs from step config.
 * Returns undefined when mode is 'inherit' (use all store upsells).
 * Returns the explicit list when mode is 'custom'.
 */
export function getAssignedUpsellOfferIds(): string[] | undefined {
  const stepConfig = getAssignedStepConfig();
  if (!stepConfig?.upsellOffers) return undefined;
  if (stepConfig.upsellOffers.mode !== 'custom') return undefined;
  return stepConfig.upsellOffers.enabledUpsellIds;
}

export interface FunnelClientConfig {
  apiClient: ApiClient;
  debugMode?: boolean;
  pluginConfig?: PluginConfig;
  environment?: {
    environment: 'local' | 'development' | 'production' | 'staging';
  };
  /**
   * Automatically redirect to result.url after navigation (default: true)
   * Set to false if you want to handle navigation manually
   */
  autoRedirect?: boolean;
  /**
   * Override funnelId from rawPluginConfig
   */
  funnelId?: string;
  /**
   * Override stepId from rawPluginConfig
   */
  stepId?: string;
}

export interface FunnelState {
  context: SimpleFunnelContext | null;
  isLoading: boolean;
  isInitialized: boolean;
  isNavigating: boolean;
  error: Error | null;
  sessionError: Error | null;
}

export class FunnelClient {
  public state: FunnelState;
  private resource: FunnelResource;
  private eventDispatcher = new EventDispatcher<FunnelState>();
  private config: FunnelClientConfig;

  // Guards
  private isInitializing = false;
  private initializationAttempted = false;

  constructor(config: FunnelClientConfig) {
    this.config = config;
    this.resource = new FunnelResource(config.apiClient);

    this.state = {
      context: null,
      isLoading: false,
      isInitialized: false,
      isNavigating: false,
      error: null,
      sessionError: null,
    };
  }

  /**
   * Update configuration (e.g. when plugin config loads)
   */
  setConfig(config: Partial<FunnelClientConfig>) {
    this.config = { ...this.config, ...config };
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: (state: FunnelState) => void): () => void {
    return this.eventDispatcher.subscribe(listener);
  }

  /**
   * Get current state
   */
  getState(): FunnelState {
    return this.state;
  }

  /**
   * Get the session ID that would be used for initialization (URL params or cookie)
   * This allows getting the session ID even before the client is fully initialized.
   */
  getDetectedSessionId(): string | null {
    // Priority 1: Already initialized session
    if (this.state.context?.sessionId) {
      return this.state.context.sessionId;
    }

    if (typeof window === 'undefined') return null;

    // Priority 2: URL params
    const params = new URLSearchParams(window.location.search);
    const urlSessionId = params.get('funnelSessionId');
    if (urlSessionId) return urlSessionId;

    // Priority 3: Cookie
    return getFunnelSessionCookie() || null;
  }

  /**
   * Reset initialization state (used for back-button restores)
   */
  resetInitialization() {
    this.initializationAttempted = false;
    this.isInitializing = false;
    // Clear context to force a fresh autoInitialize call to hit the backend
    this.updateState({
      context: null,
      isInitialized: false,
    });
  }

  /**
   * Initialize session with automatic detection (cookies, URL, etc.)
   */
  async autoInitialize(
    authSession: { customerId: string; sessionId: string },
    store: { id: string; accountId: string },
    funnelId?: string
  ): Promise<SimpleFunnelContext | null> {
    if (this.state.context) return this.state.context;
    if (this.isInitializing) return null;
    if (this.initializationAttempted) return null;

    this.initializationAttempted = true;
    this.isInitializing = true;
    this.updateState({ isLoading: true, error: null });

    try {
      // 🎯 Get detected session ID
      const existingSessionId = this.getDetectedSessionId();

      // URL params for funnelId
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      const urlFunnelId = params.get('funnelId');
      const effectiveFunnelId = urlFunnelId || funnelId;

      // 🎯 Read funnel tracking data from injected HTML
      const injectedFunnelId = getAssignedFunnelId(); // Funnel ID from server
      const funnelVariantId = getAssignedFunnelVariant(); // A/B test variant ID
      const injectedStepId = getAssignedFunnelStep(); // Current step ID

      // 🎯 Get SDK override parameters (draft, funnelTracking, etc.)
      const sdkParams = getSDKParams();

      // Priority: config override > injected > URL/prop
      const finalFunnelId = this.config.funnelId || injectedFunnelId || effectiveFunnelId;
      const finalStepId = this.config.stepId || injectedStepId;

      // 🎯 Determine funnelEnv from URL params
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const funnelEnv = urlParams?.get('funnelEnv') as 'staging' | 'production' | null;

      if (this.config.debugMode) {
        console.log('🚀 [FunnelClient] Auto-initializing...', {
          existingSessionId,
          effectiveFunnelId: finalFunnelId,
          funnelVariantId, // 🎯 Log variant ID for debugging
          funnelStepId: finalStepId, // 🎯 Log step ID for debugging
          draft: sdkParams.draft, // 🎯 Log draft mode
          funnelTracking: sdkParams.funnelTracking, // 🎯 Log tracking flag
          funnelEnv, // 🎯 Log funnel environment
          tagadaClientEnv: sdkParams.tagadaClientEnv, // 🎯 Log client environment
          tagadaClientBaseUrl: sdkParams.tagadaClientBaseUrl, // 🎯 Log custom API URL
          source: {
            funnelId: this.config.funnelId ? 'config' : injectedFunnelId ? 'injected' : effectiveFunnelId ? 'url/prop' : 'none',
            stepId: this.config.stepId ? 'config' : injectedStepId ? 'injected' : 'none',
          },
        });
      }

      // Note: We proceed even without funnelId/sessionId - the backend will create a new anonymous session if needed

      const response = await this.resource.initialize({
        cmsSession: {
          customerId: authSession.customerId,
          sessionId: authSession.sessionId,
          storeId: store.id,
          accountId: store.accountId,
        },
        funnelId: finalFunnelId,
        existingSessionId: existingSessionId || undefined,
        currentUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        funnelVariantId, // 🎯 Pass A/B test variant ID to backend
        funnelStepId: finalStepId, // 🎯 Pass step ID to backend (with config override)
        draft: sdkParams.draft, // 🎯 Pass draft mode explicitly (more robust than URL parsing)
        funnelTracking: sdkParams.funnelTracking, // 🎯 Pass funnel tracking flag explicitly
        funnelEnv: funnelEnv || undefined, // 🎯 Pass funnel environment (staging/production)
        tagadaClientEnv: sdkParams.tagadaClientEnv, // 🎯 Pass client environment override
        tagadaClientBaseUrl: sdkParams.tagadaClientBaseUrl, // 🎯 Pass custom API base URL
        currency: sdkParams.currency, // 🌍 Pass display currency override
        locale: sdkParams.locale, // 🌍 Pass display locale override
      });

      if (response.success && response.context) {
        const enriched = this.enrichContext(response.context);
        this.handleSessionSuccess(enriched);

        lazyInjectPreviewIndicator();

        return enriched;
      } else {
        throw new Error(response.error || 'Failed to initialize funnel session');
      }

    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.updateState({ error: err, isLoading: false });
      if (this.config.debugMode) {
        console.error('❌ [FunnelClient] Init failed:', err);
      }
      throw err;
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Manual initialization
   */
  async initialize(
    authSession: { customerId: string; sessionId: string },
    store: { id: string; accountId: string },
    funnelId?: string,
    entryStepId?: string
  ) {
    this.updateState({ isLoading: true, error: null });
    try {
      // 🎯 Read A/B test variant ID and step ID from injected HTML
      const funnelVariantId = getAssignedFunnelVariant();
      const funnelStepId = getAssignedFunnelStep();

      if (this.config.debugMode) {
        if (funnelVariantId) console.log('🎯 [FunnelClient] Detected A/B test variant:', funnelVariantId);
        if (funnelStepId) console.log('🎯 [FunnelClient] Detected step ID:', funnelStepId);
      }

      const response = await this.resource.initialize({
        cmsSession: {
          customerId: authSession.customerId,
          sessionId: authSession.sessionId,
          storeId: store.id,
          accountId: store.accountId,
        },
        funnelId: funnelId,
        entryStepId,
        currentUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        funnelVariantId, // 🎯 Pass A/B test variant ID to backend
        funnelStepId, // 🎯 Pass step ID to backend
      });

      if (response.success && response.context) {
        const enriched = this.enrichContext(response.context);
        this.handleSessionSuccess(enriched);

        lazyInjectPreviewIndicator();

        return enriched;
      } else {
        throw new Error(response.error || 'Failed to initialize');
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.updateState({ error: err, isLoading: false });
      throw err;
    }
  }

  /**
   * Navigate
   * @param event - Navigation event/action
   * @param options - Navigation options
   * @param options.fireAndForget - If true, queues navigation to QStash and returns immediately without waiting for result
   * @param options.customerTags - Customer tags to set (merged with existing customer tags)
   * @param options.deviceId - Device ID for geo/device tag enrichment (optional, rarely needed)
   * @param options.autoRedirect - Override global autoRedirect setting for this specific call (default: use config)
   */
  async navigate(
    event: FunnelAction,
    options?: {
      fireAndForget?: boolean;
      customerTags?: string[];
      deviceId?: string;
      autoRedirect?: boolean;
      waitForSession?: boolean;
    }
  ): Promise<FunnelNavigationResult> {
    // Wait for session if requested
    if (options?.waitForSession && !this.state.context?.sessionId) {
      if (this.config.debugMode) {
        console.log('⏳ [FunnelClient] Waiting for session before navigation...');
      }

      const maxWaitTime = 5000;
      const startTime = Date.now();

      while (!this.state.context?.sessionId && (Date.now() - startTime < maxWaitTime)) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (this.state.context?.sessionId && this.config.debugMode) {
        console.log('✅ [FunnelClient] Session ready, proceeding with navigation');
      }
    }

    if (!this.state.context?.sessionId) throw new Error('No active session');

    this.updateState({ isNavigating: true, isLoading: true });

    try {
      // Get current funnel state from injected HTML/meta tags
      let funnelVariantId = getAssignedFunnelVariant();
      let funnelStepId = getAssignedFunnelStep();
      const currentUrl = typeof window !== 'undefined' ? window.location.href : undefined;

      // ✅ FALLBACK: Use config values if injection not available (e.g., on Shopify storefront)
      if (!funnelStepId && this.config.stepId) {
        funnelStepId = this.config.stepId;
        if (this.config.debugMode) {
          console.log('🔍 [FunnelClient.navigate] Using stepId from config (no injection):', funnelStepId);
        }
      }

      if (!funnelVariantId && (this.config as any).variantId) {
        funnelVariantId = (this.config as any).variantId;
        if (this.config.debugMode) {
          console.log('🔍 [FunnelClient.navigate] Using variantId from config (no injection):', funnelVariantId);
        }
      }

      // ✅ DEBUG: Log what we're sending
      if (this.config.debugMode) {
        console.log('🔍 [FunnelClient.navigate] Sending to backend:', {
          sessionId: this.state.context.sessionId,
          currentUrl,
          funnelStepId: funnelStepId || '(not found)',
          funnelVariantId: funnelVariantId || '(not found)',
          hasInjectedStepId: !!getAssignedFunnelStep(),
          hasInjectedVariantId: !!getAssignedFunnelVariant(),
          usedConfigFallback: !getAssignedFunnelStep() && !!this.config.stepId,
          customerTags: options?.customerTags || '(none)',
          deviceId: options?.deviceId || '(none)',
        });
      }

      const fireAndForget = options?.fireAndForget || false;

      const response = await this.resource.navigate({
        sessionId: this.state.context.sessionId,
        event,
        currentUrl,
        funnelStepId,
        funnelVariantId,
        fireAndForget,
        customerTags: options?.customerTags,
        deviceId: options?.deviceId,
      });

      if (!response.success || !response.result) {
        throw new Error(response.error || 'Navigation failed');
      }

      const result = response.result as FunnelNavigationResult;

      // 🔥 Fire-and-forget mode: Just return acknowledgment, skip everything else
      if (result.queued) {
        this.updateState({ isNavigating: false, isLoading: false });

        // Update session ID if it changed (session recovery)
        if (result.sessionId && result.sessionId !== this.state.context?.sessionId) {
          if (this.config.debugMode) {
            console.log(`🔥 [FunnelClient] Session ID updated: ${this.state.context?.sessionId} → ${result.sessionId}`);
          }
          // Update context session ID
          if (this.state.context) {
            this.state.context.sessionId = result.sessionId;
          }
        }

        if (this.config.debugMode) {
          console.log('🔥 [FunnelClient] Navigation queued (fire-and-forget mode)');
        }

        return result;
      }

      // Normal navigation: handle redirect
      // Per-call option takes precedence over global config
      const shouldAutoRedirect = options?.autoRedirect !== undefined
        ? options.autoRedirect
        : this.config.autoRedirect !== false; // Default to true

      // Skip refreshSession if auto-redirecting (next page will initialize with fresh state)
      // Only refresh if staying on same page (autoRedirect: false)
      // if (!shouldAutoRedirect) {
      //   if (this.config.debugMode) {
      //     console.log('🔄 [FunnelClient] Refreshing session (no auto-redirect)');
      //   }
      //   await this.refreshSession();
      // }

      this.updateState({ isNavigating: false, isLoading: false });

      // Auto-redirect if enabled and result has a URL
      if (shouldAutoRedirect && result?.url && typeof window !== 'undefined') {
        if (this.config.debugMode) {
          console.log('🚀 [FunnelClient] Auto-redirecting to:', result.url, '(skipped session refresh - next page will initialize)');
        }
        window.location.replace(result.url);
      }

      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.updateState({ error: err, isNavigating: false, isLoading: false });
      throw err;
    }
  }

  /**
   * Go to a specific step (direct navigation)
   * @param stepId - Target step ID
   * @param options - Navigation options (autoRedirect, etc.)
   */
  async goToStep(
    stepId: string,
    options?: { autoRedirect?: boolean }
  ): Promise<FunnelNavigationResult> {
    return this.navigate(
      {
        type: FunnelActionType.DIRECT_NAVIGATION,
        data: { targetStepId: stepId },
      } as FunnelAction,
      options
    );
  }

  /**
   * Refresh session data
   */
  async refreshSession() {
    if (!this.state.context?.sessionId) return;

    try {
      const response = await this.resource.getSession(this.state.context.sessionId);
      if (response.success && response.context) {
        const enriched = this.enrichContext(response.context);
        this.updateState({ context: enriched, sessionError: null });
        return enriched;
      }
    } catch (error) {
      this.updateState({ sessionError: error instanceof Error ? error : new Error(String(error)) });
    }
  }

  /**
   * Update context data
   */
  async updateContext(updates: Partial<SimpleFunnelContext>) {
    if (!this.state.context?.sessionId) throw new Error('No active session');
    this.updateState({ isLoading: true });

    try {
      const response = await this.resource.updateContext(this.state.context.sessionId, { contextUpdates: updates });
      if (response.success) {
        await this.refreshSession();
      } else {
        throw new Error(response.error || 'Failed to update context');
      }
    } finally {
      this.updateState({ isLoading: false });
    }
  }

  /**
   * End session
   */
  async endSession() {
    if (!this.state.context?.sessionId) return;

    try {
      await this.resource.endSession(this.state.context.sessionId);
    } finally {
      this.state.context = null;
      this.updateState({ context: null, isInitialized: false });
      if (typeof document !== 'undefined') {
        // Clear cookie via import or manually if needed, but we have utility for that
        // We should probably import clearFunnelSessionCookie
      }
    }
  }

  // Private helpers

  private updateState(updates: Partial<FunnelState>) {
    this.state = { ...this.state, ...updates };
    this.eventDispatcher.notify(this.state);
  }

  private handleSessionSuccess(context: SimpleFunnelContext) {
    setFunnelSessionCookie(context.sessionId);
    this.updateState({
      context,
      isLoading: false,
      isInitialized: true,
      error: null,
      sessionError: null
    });
  }

  private enrichContext(ctx: SimpleFunnelContext): SimpleFunnelContext {
    const env = this.config.environment?.environment || detectEnvironment();

    if (env !== 'local') return ctx;

    const localResources = this.config.pluginConfig?.staticResources || {};
    if (Object.keys(localResources).length === 0) return ctx;

    // 🎯 Check if context already has the same static resources
    // This prevents creating new objects unnecessarily, which would trigger React re-renders
    const existingStatic = ctx.static || {};
    const hasAllResources = Object.keys(localResources).every(
      key => existingStatic[key] === localResources[key]
    );

    // If context already has all static resources with same references, don't recreate
    if (hasAllResources && Object.keys(existingStatic).length === Object.keys(localResources).length) {
      return ctx;
    }

    return {
      ...ctx,
      static: {
        ...localResources,
        ...existingStatic
      }
    };
  }
}

